import { isFulfilled } from '@reduxjs/toolkit';
import { SALES_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCopilotV2SwitchAblyThreads } from '../../ably/hooks/useCopilotV2SwitchAblyThreads';
import { resetActiveActionPrompt } from '../../chat/slices/promptUser';
import { useIsUngatedForCopilotV2 } from '../../gating/hooks/useIsUngatedForCopilotV2';
import { clearSelectedTemplate } from '../../promptTemplates/slices/promptTemplates';
import { setCopilotThreadIdInSuperstore } from '../../superstore/utils/copilotThreadId';
import { clearDraftPromptTemplateFromSuperstore } from '../../superstore/utils/promptTemplate';
import { setDraftMessageInSuperstore } from '../../superstore/utils/userDraftMessage';
import { isDfcMutationResult } from '../../types/dfcTypeGuards';
import { usageTracker } from '../../utils/usageTracker';
import { createThread } from '../actions/createThread';
import { useHostAppContext } from '../../hostAppContext/hooks/useAppContext';

/**
 * Consolidate the logic for creating a new thread, usage tracking,
 * updating websocket subscriptions, and focusing the input.
 *
 * @returns A function that creates a new thread and focuses the input.
 */
export const useCreateNewThread = () => {
  const dispatch = useDispatch();
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const sendFocusInput = useSendOnCopilotChannel('FOCUS_INPUT');
  const switchAblyThread = useCopilotV2SwitchAblyThreads();
  const {
    isPreviewMode
  } = useHostAppContext();
  return useCallback(({
    usageTrackerScreen,
    usageTrackerSource,
    assistantType,
    customCopilotId
  }) => {
    usageTracker.track('chatspotCoreInteraction', {
      action: 'thread_created',
      screen: usageTrackerScreen,
      source: usageTrackerSource
    });
    // TODO: Consider renaming ChatChannel and other related components to AblyChannel to differentiate them from the Copilot Channel.

    if (isUngatedForCopilotV2 && assistantType !== SALES_COPILOT) {
      switchAblyThread({
        assistantType,
        customCopilotId
      }).then(async response => {
        dispatch(resetActiveActionPrompt());
        dispatch(clearSelectedTemplate());
        await setDraftMessageInSuperstore('');
        await clearDraftPromptTemplateFromSuperstore();

        // Check if it looks like a DFC mutation result or a Redux Toolkit action result
        if (isDfcMutationResult(response)) {
          await setCopilotThreadIdInSuperstore(response.data.result.thread.id);
        } else if (isFulfilled(response)) {
          await setCopilotThreadIdInSuperstore(response.payload.thread.id);
        }
      }).catch(e => {
        Raven.captureException(e, {
          extra: {
            component: 'useCreateNewThread',
            action: 'onThreadItemClick',
            messagesVersion: 'copilotV2'
          }
        });
      });
    } else {
      // Create thread through the API only when Ably is not enabled
      dispatch(createThread({
        assistantType,
        customCopilotId
      })).then(async response => {
        if (isFulfilled(response)) {
          await setCopilotThreadIdInSuperstore(response.payload.id);
          await setDraftMessageInSuperstore('');
        }
      }).catch(e => Raven.captureException(e));
    }
    if (!isPreviewMode) {
      sendFocusInput();
    }
  }, [dispatch, isUngatedForCopilotV2, sendFocusInput, switchAblyThread, isPreviewMode]);
};